import { EditorState, Extension } from "@codemirror/state"
import {
  drawSelection,
  dropCursor,
  EditorView,
  highlightActiveLine,
  keymap,
  lineNumbers,
  ViewUpdate,
  highlightActiveLineGutter,
} from "@codemirror/view"
import { useMemo } from "react"
import { history } from "@codemirror/commands"
import { closeBrackets, closeBracketsKeymap } from "@codemirror/autocomplete"
import { highlightSelectionMatches } from "@codemirror/search"
import { bracketMatching, foldGutter, indentOnInput } from "@codemirror/language"
import type { CursorPosition } from "../code-editor"

interface BaseExtensionsOptions {
  includeKeymap?: boolean,
  includeLineNumbers?: boolean,
  lineWrapping?: boolean,
  onChange?: (value: string, viewUpdate: ViewUpdate) => void,
  onCursorChange?: (position: CursorPosition) => void,
  onEditorUpdate?: (update: ViewUpdate) => void,
  onEndChange?: (value: string) => void,
  onSelectionChange?: (characterCount: number) => void,
  placeholder?: string,
  readonly?: boolean,
  theme?: Extension
}

/**
 * 提供代码编辑器的基础扩展集合
 * 包括：行号、历史、括号匹配、折叠、缩进等
 * 
 * @param options - 配置选项
 * @returns 基础扩展数组
 */
export function useBaseExtensions(options: BaseExtensionsOptions = {}): Extension[] {
  const {
    readonly = false,
    lineWrapping = true,
    onChange,
    onCursorChange,
    onEndChange,
    onSelectionChange,
    onEditorUpdate,
    theme,
    includeLineNumbers = true,
    includeKeymap = true,
  } = options

  const extensions = useMemo(() => {
    const baseExtensions: Extension[] = []

    // 基础视图扩展
    if (includeLineNumbers) {
      baseExtensions.push(lineNumbers())
    }
    
    baseExtensions.push(
      highlightActiveLineGutter(),
      highlightActiveLine(),
    )

    // 行换行
    if (lineWrapping) {
      baseExtensions.push(EditorView.lineWrapping)
    }

    // 主题
    if (theme) {
      baseExtensions.push(theme)
    }

    // 只读模式
    if (readonly) {
      baseExtensions.push(
        EditorState.readOnly.of(true),
        EditorView.editable.of(false)
      )
    } else {
      // 编辑功能扩展
      baseExtensions.push(
        history(),
        foldGutter({
          markerDOM: (open) => {
            const svgNS = "http://www.w3.org/2000/svg"
            const wrapper = document.createElement("div")
            wrapper.classList.add("cm-fold-marker")
            wrapper.style.cursor = "default"
            const svgElement = document.createElementNS(svgNS, "svg")
            svgElement.setAttribute("viewBox", "0 0 10 10")
            svgElement.setAttribute("width", "6")
            svgElement.setAttribute("height", "6")
            const pathElement = document.createElementNS(svgNS, "path")
            const d = open ? "M1 3 L5 7 L9 3" : "M3 1 L7 5 L3 9"
            pathElement.setAttribute("d", d)
            pathElement.setAttribute("fill", "none")
            pathElement.setAttribute("stroke", "currentColor")
            pathElement.setAttribute("stroke-width", "1.5")
            pathElement.setAttribute("stroke-linecap", "round")
            svgElement.appendChild(pathElement)
            wrapper.appendChild(svgElement)
            return wrapper
          },
        }),
        indentOnInput(),
        bracketMatching(),
        closeBrackets(),
        highlightSelectionMatches({ minSelectionLength: 2 }),
        drawSelection(),
        dropCursor(),
        EditorState.allowMultipleSelections.of(true),
        EditorView.clickAddsSelectionRange.of(
          (event) => event.altKey && !event.metaKey && !event.shiftKey,
        ),
      )

      // 键盘映射（如果需要）
      if (includeKeymap) {
        baseExtensions.push(
          keymap.of(closeBracketsKeymap),
        )
      }

      // 事件监听器
      if (onChange || onCursorChange || onSelectionChange || onEditorUpdate) {
        baseExtensions.push(
          EditorView.updateListener.of((update: ViewUpdate) => {
            // 触发编辑器更新回调
            onEditorUpdate?.(update)

            // 文档变化回调
            if (onChange && update.docChanged) {
              const newValue = update.state.doc.toString()
              onChange(newValue, update)
            }

            // 光标位置变化回调
            if (onCursorChange && update.selectionSet) {
              const { state } = update
              const pos = state.selection.main.head
              const lineInfo = state.doc.lineAt(pos)

              onCursorChange({
                column: pos - lineInfo.from + 1,
                line: lineInfo.number,
              })
            }

            // 选中字符数变化回调
            if (onSelectionChange && update.selectionSet) {
              const { state } = update
              const { from, to } = state.selection.main
              const characterCount = to - from

              onSelectionChange(characterCount)
            }
          })
        )
      }

      // 结束触发：在编辑器失焦时回调最终内容
      if (onEndChange) {
        baseExtensions.push(
          EditorView.domEventHandlers({
            blur: (_event, view) => {
              const value = view.state.doc.toString()
              onEndChange(value)
            },
          })
        )
      }
    }

    return baseExtensions
  }, [
    readonly,
    lineWrapping,
    onChange,
    onCursorChange,
    onEndChange,
    onSelectionChange,
    onEditorUpdate,
    theme,
    includeLineNumbers,
    includeKeymap,
  ])

  return extensions
}

